import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Badge,
} from '@alertwhale/ui'
import { useApplications, useCreateApplication } from '@/hooks/useApplications'
import { Plus, AppWindow, Loader2, Smartphone, RefreshCw, Apple, Cpu } from 'lucide-react'
import { formatDate, cn } from '@/lib/utils'

export function ApplicationsPage() {
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [name, setName] = useState('')
  const [iosBundleId, setIosBundleId] = useState('')
  const [androidPackageName, setAndroidPackageName] = useState('')

  const { data, isLoading, refetch } = useApplications()
  const createApp = useCreateApplication()

  const handleRefresh = async () => {
    setIsRefreshing(true)
    await refetch()
    setIsRefreshing(false)
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    await createApp.mutateAsync({
      name,
      iosBundleId: iosBundleId || undefined,
      androidPackageName: androidPackageName || undefined,
    })
    setIsCreateOpen(false)
    setName('')
    setIosBundleId('')
    setAndroidPackageName('')
  }

  const applications = data || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            <span className="text-gradient">Applications</span>
          </h1>
          <p className="text-muted-foreground mt-1">Manage your mobile applications</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
            Refresh
          </Button>
          <Button variant="glow" onClick={() => setIsCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            Create Application
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">Loading applications...</span>
          </div>
        </div>
      ) : applications.length === 0 ? (
        <Card className="glass-card border-0">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
              <AppWindow className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No applications yet</h3>
            <p className="text-muted-foreground text-center mb-6 max-w-md">
              Create your first application to start sending notifications to your users.
            </p>
            <Button variant="glow" onClick={() => setIsCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              Create Your First Application
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {applications.map((app) => (
            <Link key={app.id} to={`/applications/${app.id}`}>
              <Card className="glass-card border-0 hover:border-primary/30 transition-all group h-full">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <Smartphone className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg truncate group-hover:text-primary transition-colors">
                        {app.name}
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Created {formatDate(app.created_at)}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2 flex-wrap">
                    {app.ios_bundle_id && (
                      <Badge variant="outline" className="text-xs bg-white/[0.03]">
                        <Apple className="h-3 w-3 mr-1" />
                        iOS
                      </Badge>
                    )}
                    {app.android_package_name && (
                      <Badge variant="outline" className="text-xs bg-white/[0.03]">
                        <Cpu className="h-3 w-3 mr-1" />
                        Android
                      </Badge>
                    )}
                    {app.apns_key_id && (
                      <Badge variant="secondary" className="text-xs bg-success/10 text-success border-success/20">
                        APNs ✓
                      </Badge>
                    )}
                    {app.fcm_credentials_encrypted && (
                      <Badge variant="secondary" className="text-xs bg-success/10 text-success border-success/20">
                        FCM ✓
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="glass-card border-white/10 sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Create Application</DialogTitle>
            <DialogDescription>
              Add a new mobile application to send notifications
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-5 py-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">
                Application Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                placeholder="My App"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="iosBundleId" className="text-sm font-medium">
                iOS Bundle ID
              </Label>
              <Input
                id="iosBundleId"
                placeholder="com.company.app"
                value={iosBundleId}
                onChange={(e) => setIosBundleId(e.target.value)}
                              />
              <p className="text-xs text-muted-foreground">
                Required for iOS push notifications
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="androidPackageName" className="text-sm font-medium">
                Android Package Name
              </Label>
              <Input
                id="androidPackageName"
                placeholder="com.company.app"
                value={androidPackageName}
                onChange={(e) => setAndroidPackageName(e.target.value)}
                              />
              <p className="text-xs text-muted-foreground">
                Required for Android push notifications
              </p>
            </div>
            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateOpen(false)}
                disabled={createApp.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createApp.isPending || !name.trim()}>
                {createApp.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    Create Application
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
